import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { AssistantChatProvider } from '@/components/assistant/assistant-chat-context';
import { AssistantDrawer } from '@/components/assistant/assistant-drawer';
import { Header } from '@/components/layout/header';
import { PageErrorBoundary } from '@/components/layout/page-error-boundary';
import { Sidebar } from '@/components/layout/sidebar';
import { ManagedModeProvider } from '@/components/layout/managed-mode-provider';
import { WelcomeLanding } from '@/components/layout/welcome-landing';
import { isMobileExperiencePath } from '@/components/mobile/mobile-shell';
import { useSession } from '@/components/auth/user-provider';
import { useAuth } from '@/hooks/use-auth';
import { CustomerBrandProvider } from '@/components/tenancy/customer-brand-provider';
import { Button } from '@/components/ui/button';
import { CurrentUserGroupRecovery } from '@/components/auth/group-access-recovery';
import { SidebarVisibilityProvider, useSidebarVisibility } from '@/hooks/use-sidebar-visibility';

const SIDEBAR_STORAGE_KEY = 'cdf-sidebar-collapsed';

/** Shown while MSAL finishes startup / redirect handling. */
function SessionLoadingScreen() {
  return (
    <div className="text-muted-foreground flex min-h-svh items-center justify-center text-sm">
      Checking sign-in…
    </div>
  );
}

/**
 * Mobile experience (`/today/*`) owns its chrome via MobileShell — skip the
 * desktop header, sidebar, padding, and assistant drawer.
 */
function MobileExperienceShell() {
  const location = useLocation();
  return (
    <div className="bg-background h-svh overflow-hidden">
      <PageErrorBoundary resetKey={location.pathname}>
        <React.Suspense fallback={<div role="status" className="text-muted-foreground p-6 text-sm">Loading page…</div>}>
          <Outlet />
        </React.Suspense>
      </PageErrorBoundary>
    </div>
  );
}

function DesktopSignedInShell() {
  const location = useLocation();
  const sidebar = useSidebarVisibility();
  const [collapsed, setCollapsed] = React.useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);

  const toggleSidebar = () => {
    setCollapsed((value) => {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(!value));
      return !value;
    });
  };

  return (
    <AssistantChatProvider>
      <div className="bg-background flex h-svh flex-col">
        {/* Skip link for keyboard users (WCAG 2.4.1) — revealed on focus */}
        <a
          href="#main-content"
          className="bg-background text-foreground ring-ring sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-3 focus:py-2 focus:text-sm focus:ring-2"
        >
          Skip to main content
        </a>
        {/* Full-width sticky header — stays put while the content scrolls */}
        <Header onMobileSidebarOpen={() => setMobileSidebarOpen(true)} />
        <div className="flex min-h-0 flex-1">
          {/* Sidebar: desktop inline panel, mobile slide-in overlay */}
          <Sidebar
            collapsed={collapsed}
            onToggle={toggleSidebar}
            mobileOpen={mobileSidebarOpen}
            onMobileClose={() => setMobileSidebarOpen(false)}
            desktopHidden={!sidebar.visible}
          />
          <main
            id="main-content"
            tabIndex={-1}
            className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-4 outline-none md:p-6"
          >
            <PageErrorBoundary resetKey={location.pathname}>
              <React.Suspense fallback={<div role="status" className="text-muted-foreground p-6 text-sm">Loading page…</div>}>
                <Outlet />
              </React.Suspense>
            </PageErrorBoundary>
          </main>
        </div>
        <AssistantDrawer />
      </div>
    </AssistantChatProvider>
  );
}

function SignedInShell() {
  const location = useLocation();
  return (
    <CustomerBrandProvider>
      <ManagedModeProvider>
        {isMobileExperiencePath(location.pathname) ? <MobileExperienceShell /> : <SidebarVisibilityProvider><DesktopSignedInShell /></SidebarVisibilityProvider>}
      </ManagedModeProvider>
    </CustomerBrandProvider>
  );
}

function SessionAccessError({ forbidden }: { forbidden: boolean }) {
  const { reconnect, logout, ready, interactionError } = useAuth();
  const { retryAccess } = useSession();
  return (
    <main className="bg-background flex min-h-svh items-center justify-center p-6">
      <section className="max-w-lg space-y-4" aria-labelledby="access-error-title">
        <h1 id="access-error-title" className="text-xl font-semibold">{forbidden ? 'Application access unavailable' : 'Your session may have expired'}</h1>
        <p className="text-muted-foreground">
          {forbidden
            ? 'Your account does not have the required application permission. Contact your administrator.'
            : 'Reconnect to continue, or retry the connection. If the problem continues, contact your administrator.'}
        </p>
        <CurrentUserGroupRecovery />
        {interactionError ? <p role="alert">{interactionError}</p> : null}
        <div className="flex flex-wrap gap-2">
          <Button disabled={!ready} onClick={retryAccess}>Retry connection</Button>
          {!forbidden ? <Button variant="outline" disabled={!ready} onClick={reconnect}>Reconnect</Button> : null}
          <Button variant="ghost" disabled={!ready} onClick={logout}>Sign out</Button>
        </div>
      </section>
    </main>
  );
}

export function AppShell() {
  const { session } = useSession();

  if (session.status === 'accessError') {
    return <SessionAccessError forbidden={session.forbidden} />;
  }

  if (session.status === 'loading') {
    return <SessionLoadingScreen />;
  }

  if (session.status === 'signedOut') {
    return <WelcomeLanding />;
  }

  return <SignedInShell key={session.user.appUserId ?? session.user.email} />;
}
